import {
  filterRealRouteStations,
  NO_VALUE_DISPLAY,
  type ICourseStationInfo,
  type IDropdownItem,
} from "@ryde/shared";

import {
  COURSE_CSV_SEPARATORS,
  type ICourseScheduleCsvParams,
} from "./course-csv.models";

/** One `Name (#ID)` line per station in route order; the id lets an upload resolve renamed or same-named stations. */
export const formatCourseStationsCsvValue = (
  stationsInfo: ICourseStationInfo[] | null | undefined,
): string => {
  const lines = filterRealRouteStations(stationsInfo ?? []).map(
    station => `${station.name.trim()} (#${station.id})`,
  );

  return lines.length
    ? lines.join(COURSE_CSV_SEPARATORS.station)
    : NO_VALUE_DISPLAY;
};

export const buildDropdownLabelById = (
  items: IDropdownItem[] | undefined,
): Map<number, string> =>
  new Map((items ?? []).map(item => [Number(item.value), item.label]));

const resolveNames = ({
  ids,
  nameById,
}: {
  ids: number[] | undefined;
  nameById: Map<number, string>;
}): string[] =>
  (ids ?? []).flatMap(id => {
    const name = nameById.get(id);

    return name ? [name] : [];
  });

/** Each slot as `HH:mm <direction> Day groups: … Day types: …`, the same labels the availability upload reads. */
export const formatCourseScheduleCsvValue = ({
  schedules,
  dayTypeNameById,
  dayGroupNameById,
  dayTypeLabel,
  dayGroupLabel,
}: ICourseScheduleCsvParams): string => {
  if (!schedules?.length) {
    return NO_VALUE_DISPLAY;
  }

  return schedules
    .map(schedule => {
      const dayGroupNames = resolveNames({
        ids: schedule.dayGroupIds,
        nameById: dayGroupNameById,
      });
      const dayTypeNames = resolveNames({
        ids: schedule.dayTypeIds,
        nameById: dayTypeNameById,
      });
      const segments: string[] = [schedule.time, schedule.direction];

      if (dayGroupNames.length) {
        segments.push(`${dayGroupLabel}: ${dayGroupNames.join(", ")}`);
      }

      if (dayTypeNames.length) {
        segments.push(`${dayTypeLabel}: ${dayTypeNames.join(", ")}`);
      }

      return segments.join(" ");
    })
    .join(COURSE_CSV_SEPARATORS.scheduleEntry);
};
